/**
 * v1 → v2 migration for IntegrationConfig.
 *
 * Kept in its own file (separate from integration.page.tsx) so unit tests can
 * import it without pulling the React component tree (which drags Auth/Lottie/
 * canvas deps that crash in jsdom).
 *
 * Used by:
 *   - integration.page.tsx mergeIntegration() → runs on every load
 *   - unit tests at __tests__/features-migration.test.tsx
 */

import type {
    IntegrationConfig,
    IntegrationButtonConfig,
    FeatureBlock,
    FeatureKey,
    VisualizerSettings,
    VisualizerOpenMode,
    InlineEmbedSettings,
    ArSettings,
    Model3DSettings,
    ChatSettings,
} from './config.model';

/**
 * Starting point for the in-gallery panel, used when a vendor first turns it on.
 *
 * These mirror the SDK's own fallbacks in `inlineSettings()`
 * (sdk/ardealy-sdk-v2.js) so a config written here and a config the SDK reads
 * with fields missing behave identically. `containerSelector` is deliberately
 * empty: there is no sane guess for a merchant's gallery, and the SDK warns and
 * does nothing rather than injecting into the wrong element.
 */
export function defaultInlineEmbedSettings(): InlineEmbedSettings {
    return {
        enabled: false,
        containerSelector: '',
        // The carousel is both the commoner shape and the safer default: revealing
        // a panel that was already visible is a no-op, while failing to reveal a
        // hidden one loses the feature entirely.
        containerBehavior: 'carousel',
        insertPosition: 'prepend',
        aspectRatio: '1:1',
        loadStrategy: 'visible',
        activateOnLoad: 'when_room_known',
        defaultSpaceId: '',
        defaultSpaceIdFloor: '',
        defaultSpaceIdWalls: '',
        thumbnailSelector: '',
        label: 'View in my room',
    };
}

/**
 * Narrow an open mode to the two values the legacy top-level `openMode` field —
 * and the v1 SDKs still reading it from CDN caches — understand.
 *
 * Takes a plain string rather than `VisualizerOpenMode` because it also has to
 * cope with configs persisted while the in-gallery panel was briefly modelled as
 * a third open mode, `'inline_embed'`. Those degrade to the overlay rather than
 * reaching an old SDK as a mode it cannot interpret.
 */
export function toLegacyOpenMode(mode: string | undefined): 'iframe_overlay' | 'new_tab' {
    return mode === 'new_tab' ? 'new_tab' : 'iframe_overlay';
}

/**
 * Lift a single default room onto the two per-surface ones.
 *
 * The panel used to open every product in one nominated room. That room serves
 * whichever surfaces it was analysed for, so it is the right starting value for
 * both — and where it is wrong (a floor-only room carried onto the wall slot)
 * the settings panel says so, which is more than the vendor got before.
 *
 * Only ever fills a blank: a vendor who has already chosen per-surface rooms is
 * never overwritten by the legacy field.
 */
export function splitDefaultSpace(inline: InlineEmbedSettings | undefined): InlineEmbedSettings | undefined {
    if (!inline || !inline.defaultSpaceId) return inline;
    if (inline.defaultSpaceIdFloor && inline.defaultSpaceIdWalls) return inline;
    return {
        ...inline,
        defaultSpaceIdFloor: inline.defaultSpaceIdFloor || inline.defaultSpaceId,
        defaultSpaceIdWalls: inline.defaultSpaceIdWalls || inline.defaultSpaceId,
    };
}

/**
 * Read a persisted visualizer block into the current shape.
 *
 * The in-gallery panel shipped first as a third `openMode` value, which read as
 * mutually exclusive with the buttons when it never was. It is now its own
 * `inline.enabled` flag, so a config written under the old shape has to be lifted:
 * `openMode: 'inline_embed'` means the panel was on, and the button falls back to
 * the overlay it would have used anyway.
 */
export function normalizeVisualizerSettings(
    settings: (Omit<VisualizerSettings, 'openMode' | 'mobileOpenMode'> & {
        openMode?: string
        mobileOpenMode?: string
    }) | undefined,
): VisualizerSettings | undefined {
    if (!settings) return settings as undefined;

    const wasInlineMode = settings.openMode === 'inline_embed'
        || settings.mobileOpenMode === 'inline_embed';
    const splitInline = splitDefaultSpace(settings.inline);
    if (!wasInlineMode) {
        // Nothing to lift out of `openMode`, but the single default room may still
        // need splitting — rebuilt only when it does, so the common path returns
        // the same object and callers can keep comparing by identity.
        if (splitInline === settings.inline) return settings as VisualizerSettings;
        return { ...settings, inline: splitInline } as VisualizerSettings;
    }

    const normalized: VisualizerSettings = {
        ...settings,
        openMode: toLegacyOpenMode(settings.openMode),
        mobileOpenMode: undefined,
        inline: {
            ...defaultInlineEmbedSettings(),
            ...(splitInline || {}),
            enabled: true,
        },
    };
    if (settings.mobileOpenMode) {
        normalized.mobileOpenMode = toLegacyOpenMode(settings.mobileOpenMode);
    }
    return normalized;
}

/**
 * Build a v2 feature map from a legacy button config.
 *
 * Called by `migrateIntegrationToV2` when upgrading a vendor's persisted config
 * from the v1 shape. Keeps the existing button styling as the per-feature default
 * so vendors don't see their button change visually after the upgrade.
 */
export function buildDefaultFeatures(base: IntegrationButtonConfig): Record<FeatureKey, FeatureBlock> {
    return {
        visualizer: {
            enabled: false,
            button: { ...base },
            mobileButton: null,
            settings: { openMode: 'iframe_overlay' } as VisualizerSettings,
        },
        ar: {
            enabled: false,
            button: { ...base, label: 'View in AR' },
            mobileButton: null,
            settings: { desktopFallback: 'qr', placement: 'floor' } as ArSettings,
        },
        model3d: {
            enabled: false,
            button: { ...base, label: 'View in 3D' },
            mobileButton: null,
            settings: { modalSize: 'lg', autoRotate: true } as Model3DSettings,
        },
        /*
         * The chat carries a button config like its siblings only because the
         * shape demands one — the SDK draws its own floating launcher and never
         * reads it. Everything a vendor can change about the chat is in
         * `settings`, edited on the Shopper chat page.
         */
        chat: {
            enabled: false,
            button: { ...base, label: 'Ask about this product' },
            mobileButton: null,
            settings: { position: 'right' } as ChatSettings,
        },
    };
}

/**
 * Migrate a legacy IntegrationConfig (schemaVersion undefined/1) to schemaVersion 2
 * with a populated `features` map. Idempotent — already-migrated configs are
 * returned as-is.
 *
 * The legacy top-level `button`/`openMode`/`mobileButton` fields are left intact
 * as a cache-transition shim so any v1 SDKs still served from CDN keep working
 * until caches expire (~90 days post-rollout).
 */
export function migrateIntegrationToV2(legacy: IntegrationConfig): IntegrationConfig {
    if (legacy.schemaVersion === 2 && legacy.features) {
        // Already v2, but the in-gallery panel may still be stored as a third
        // open mode from before it became its own flag. Rebuilt only when there
        // is something to lift, so the common path returns the same object.
        const visualizer = legacy.features.visualizer;
        const normalized = normalizeVisualizerSettings(
            visualizer?.settings as VisualizerSettings | undefined,
        );
        if (visualizer && normalized !== visualizer.settings) {
            return {
                ...legacy,
                features: {
                    ...legacy.features,
                    visualizer: { ...visualizer, settings: normalized },
                },
            };
        }
        return legacy;
    }
    // Fallback to a sensible default button if somehow `button` isn't set —
    // this shouldn't happen for configs coming from the backend since the entity
    // declares `button` as required, but defensive against hand-edited test
    // fixtures and legacy docs from the earliest releases.
    const base = legacy.button || ({
        containerSelector: '#visio-ardealy',
        insertPosition: 'append',
        buttonStyle: 'filled',
        label: 'Visualize in your space',
        backgroundColor: '#3B82F6',
        textColor: '#FFFFFF',
        borderColor: '#3B82F6',
        borderRadius: '8px',
        paddingH: '24',
        paddingV: '12',
        iconEnabled: true,
        iconOnly: false,
        iconSvg: '',
        fullWidth: false,
        customCSS: '',
    } as IntegrationButtonConfig);
    const features = buildDefaultFeatures(base);
    // Seed visualizer with existing legacy values — vendors' current behaviour
    // is the visualizer being the sole enabled feature.
    features.visualizer.enabled = !!legacy.isEnabled;
    features.visualizer.button = { ...base };
    features.visualizer.mobileButton = legacy.mobileButton ?? null;
    features.visualizer.settings = {
        openMode: legacy.openMode || 'iframe_overlay',
        mobileOpenMode: legacy.mobileOpenMode,
    } as VisualizerSettings;
    return {
        ...legacy,
        schemaVersion: 2,
        layoutMode: legacy.layoutMode || 'buttons',
        features,
    };
}
